<template>
  <v-container fluid class="pa-2">
    <v-card outlined class="compact-card">
      <v-card-text class="pa-3">
        <v-row dense class="mb-3">
          <v-col cols="auto">
            <v-btn 
              color="primary" 
              small
              @click="generateReport" 
              :loading="isLoading"
              class="caption"
            >
              <v-icon left x-small>mdi-refresh</v-icon> Generate
            </v-btn>
          </v-col>
          <v-col cols="auto" v-if="reportData">
            <v-btn 
              color="success" 
              small
              @click="exportExcel" 
              :loading="isExportingExcel"
              class="caption mr-1"
            >
              <v-icon left x-small>mdi-file-excel-box</v-icon> Excel
            </v-btn>
            <v-btn 
              color="error" 
              small
              @click="exportPDF" 
              :loading="isExportingPDF"
              class="caption"
            >
              <v-icon left x-small>mdi-file-pdf-box</v-icon> PDF
            </v-btn>
          </v-col>
        </v-row>

        <v-card v-if="reportData" outlined class="compact-table-card">
          <v-data-table
            :headers="headers"
            :items="sortedLeases"
            :items-per-page="10"
            class="elevation-1 compact-table"
            dense
          >
            <template v-slot:item.expiresIn="{ item }">
              <v-chip :color="getExpiryColor(item.expiresIn)" x-small>
                {{ getExpiryText(item.expiresIn) }}
              </v-chip>
            </template>
            <template v-slot:item.rentalAmount="{ item }">
              <span class="caption font-weight-medium">{{ formatCurrency(item.rentalAmount) }}</span>
            </template>
          </v-data-table>
        </v-card>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script>
import { collection, getDocs, query, where } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import { utils as XLSXUtils, writeFile as XLSXWriteFile } from 'xlsx';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

const db = getFirestore();

export default {
  name: "LeaseExpiryReport",
  props: {
    property: {
      type: Object,
      required: true
    },
    unit: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      isLoading: false,
      isExportingExcel: false,
      isExportingPDF: false,
      base64log: "",
      reportData: null,
      headers: [
        { text: 'REF', value: 'leaseReference', width: '100px' },
        { text: 'PROPERTY', value: 'propertyName' },
        { text: 'LANDLORD', value: 'landlordName' },
        { text: 'TENANT', value: 'tenantName' },
        { text: 'EXPIRES IN', value: 'expiresIn', width: '120px' },
        { text: 'RENTAL', value: 'rentalAmount', align: 'end' }
      ],
      columnDefinitions: [
        { field: 'leaseReference', header: 'REF', alwaysVisible: true },
        { field: 'propertyName', header: 'PROPERTY', alwaysVisible: true },
        { field: 'landlordName', header: 'LANDLORD', alwaysVisible: true },
        { field: 'tenantName', header: 'TENANT', alwaysVisible: true },
        { 
          field: 'expiresIn', 
          header: 'EXPIRES IN', 
          alwaysVisible: true,
          formatter: (value) => this.getExpiryText(value)
        },
        { 
          field: 'rentalAmount', 
          header: 'RENTAL', 
          alwaysVisible: true,
          formatter: (value) => this.formatCurrency(value),
          isTotalable: true
        }
      ],
      visibleColumns: {
        leaseReference: true,
        propertyName: true,
        landlordName: true,
        tenantName: true,
        expiresIn: true,
        rentalAmount: true
      }
    };
  },
  computed: {
    propertyName() {
      return this.property?.displayName || 'All Properties';
    },
    sortedLeases() {
      if (!this.reportData) return [];
      return this.reportData.sort((a, b) => {
        return new Date(a.leaseDetails.endDate) - new Date(b.leaseDetails.endDate);
      });
    },
    reportTitle() {
      return 'Lease Expiry Report';
    },
    startDate() {
      return new Date().toISOString().split('T')[0];
    },
    endDate() {
      return new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0];
    }
  },
  async created() {
    await this.fetchLogoBase64();
  },
  methods: {
    async fetchLogoBase64() {
      const logosRef = collection(db, 'Logo');
      const q = query(logosRef, where('id', '==', 2));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const logoDoc = querySnapshot.docs[0];
        const logoData = logoDoc.data();
        this.base64log = logoData.logo2;
      } else {
        console.error('Logo not found');
      }
    },

    async getLandlordDisplayName(propertyId) {
      try {
        const propertyQuery = query(collection(db, "properties"), where("id", "==", propertyId));
        const propertySnapshot = await getDocs(propertyQuery);

        if (!propertySnapshot.empty) {
          const propertyData = propertySnapshot.docs[0].data();
          const landlordRef = propertyData.selectedLandlord;

          if (landlordRef) {
            const landlordQuery = query(collection(db, "landlords"), where("reference", "==", landlordRef));
            const landlordSnapshot = await getDocs(landlordQuery);

            if (!landlordSnapshot.empty) {
              const landlordData = landlordSnapshot.docs[0].data();
              return landlordData.displayAs || "Not Available";
            }
          }
        }
        return "Not Available";
      } catch (error) {
        console.error("Error fetching landlord name:", error);
        return "Error Loading";
      }
    },

    async generateReport() {
      this.isLoading = true;
      try {
        let leasesQuery = query(collection(db, "Leases"));
        const leasesSnapshot = await getDocs(leasesQuery);
        const today = new Date();

        const reportDataPromises = leasesSnapshot.docs.map(async (doc) => {
          const data = doc.data();
          const endDate = new Date(data.leaseDetails.endDate);
          const expiresIn = Math.ceil((endDate - today) / (1000 * 60 * 60 * 24));

          const landlordName = await this.getLandlordDisplayName(data.propertyId);

          return {
            ...data,
            landlordName,
            expiresIn,
            rentalAmount: parseFloat(data.leaseDetails.rentalAmount) || 0
          };
        });

        this.reportData = await Promise.all(reportDataPromises);
      } catch (error) {
        console.error("Error generating lease expiry report:", error);
        this.$toast?.error?.("Failed to generate lease expiry report");
      } finally {
        this.isLoading = false;
      }
    },

    getExpiryColor(days) {
      if (days < 0) return 'error';
      if (days <= 30) return 'warning';
      if (days <= 60) return 'info';
      if (days <= 90) return 'primary';
      return 'success';
    },
    
    getExpiryText(days) {
      if (days < 0) return 'Expired';
      if (days <= 30) return '≤ 30 days';
      if (days <= 60) return '≤ 60 days';
      if (days <= 90) return '≤ 90 days';
      return '180+ days';
    },

    formatCurrency(value) {
      if (value === null || value === undefined || isNaN(value)) return '';
      return 'R ' + new Intl.NumberFormat('en-ZA', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const options = { year: 'numeric', month: 'short', day: 'numeric' };
      return new Date(dateString).toLocaleDateString('en-ZA', options);
    },

    sumField(field) {
      if (!this.reportData) return 0;
      return this.reportData.reduce((sum, item) => {
        const value = parseFloat(item[field]) || 0;
        return sum + value;
      }, 0);
    },

    async exportExcel() {
      this.isExportingExcel = true;
      
      try {
        const headers = this.columnDefinitions
          .filter(col => this.visibleColumns[col.field] || col.alwaysVisible)
          .map(col => col.header);

        const data = this.reportData.map(item => {
          const row = {};
          this.columnDefinitions
            .filter(col => this.visibleColumns[col.field] || col.alwaysVisible)
            .forEach(col => {
              row[col.header] = col.formatter ? col.formatter(item[col.field]) : item[col.field];
            });
          return row;
        });

        const totalsRow = { 'REF': 'TOTALS', 'PROPERTY': '' };
        this.columnDefinitions
          .filter(col => this.visibleColumns[col.field] || col.alwaysVisible)
          .forEach(col => {
            if (col.isTotalable) {
              totalsRow[col.header] = this.formatCurrency(this.sumField(col.field));
            }
          });
        data.push(totalsRow);

        const ws = XLSXUtils.json_to_sheet(data, { header: headers });

        if (!ws['!cols']) ws['!cols'] = [];
        headers.forEach((header, i) => {
          ws['!cols'][i] = { width: 15 };
          const cellRef = XLSXUtils.encode_cell({ c: i, r: 0 });
          ws[cellRef].s = {
            font: { bold: true },
            fill: { fgColor: { rgb: "FFD9D9D9" } },
            alignment: { horizontal: 'center' }
          };
        });

        const totalsRowNum = data.length - 1;
        headers.forEach((_, i) => {
          const cellRef = XLSXUtils.encode_cell({ c: i, r: totalsRowNum });
          ws[cellRef].s = {
            font: { bold: true },
            fill: { fgColor: { rgb: "FFE0E0E0" } }
          };
        });

        const wb = XLSXUtils.book_new();
        XLSXUtils.book_append_sheet(wb, ws, "Lease Expiry");

        const metadata = [
          [`Report Title: ${this.reportTitle}`],
          [`Property: ${this.propertyName}`],
          [`Date Range: ${this.formatDate(this.startDate)} to ${this.formatDate(this.endDate)}`],
          [`Generated: ${new Date().toLocaleDateString()}`],
          ['']
        ];
        const wsMeta = XLSXUtils.aoa_to_sheet(metadata);
        XLSXUtils.book_append_sheet(wb, wsMeta, "Report Info");

        const fileName = `lease_expiry_report_${new Date().toISOString().slice(0, 10)}.xlsx`;
        XLSXWriteFile(wb, fileName);

        this.$toast?.success?.("Excel report exported successfully!");
      } catch (error) {
        console.error("Error generating Excel:", error);
        this.$toast?.error?.("Failed to generate Excel report");
      } finally {
        this.isExportingExcel = false;
      }
    },

    async exportPDF() {
      this.isExportingPDF = true;
      
      try {
        const fonts = {
          Roboto: {
            normal: 'assets/Roboto/static/Roboto-Regular.ttf',
            bold: 'assets/Roboto/static/Roboto-Bold.ttf',
            italics: 'assets/Roboto/static/Roboto-Italic.ttf',
            bolditalics: 'assets/Roboto/static/Roboto-BoldItalic.ttf'
          }
        };

        pdfMake.fonts = fonts;

        const columnHeaders = this.columnDefinitions
          .filter(col => this.visibleColumns[col.field] || col.alwaysVisible)
          .map(col => ({ text: col.header, style: 'tableHeader' }));

        const tableBody = [columnHeaders];

        this.reportData.forEach(item => {
          const row = this.columnDefinitions
            .filter(col => this.visibleColumns[col.field] || col.alwaysVisible)
            .map(col => {
              const value = col.formatter ? col.formatter(item[col.field]) : item[col.field];
              return { text: value, style: 'tableData' };
            });
          
          tableBody.push(row);
        });

        const totalsRow = this.columnDefinitions
          .filter(col => this.visibleColumns[col.field] || col.alwaysVisible)
          .map(col => {
            if (col.isTotalable) {
              return { text: this.formatCurrency(this.sumField(col.field)), style: 'tableFooter' };
            }
            return { text: col.field === 'leaseReference' ? 'TOTALS' : '', style: 'tableFooter' };
          });
        
        tableBody.push(totalsRow);

        const columnCount = columnHeaders.length;
        const columnWidth = 100 / columnCount;
        const columnWidths = Array(columnCount).fill(`${columnWidth}%`);

        const docDefinition = {
          defaultStyle: {
            font: 'Roboto'
          },
          content: [
            this.generateHeaderSection(),
            this.generateReportInfoSection(),
            {
              table: {
                headerRows: 1,
                widths: columnWidths,
                body: tableBody
              },
              layout: this.getTableLayout(),
              margin: [0, 0, 0, 15]
            },
            this.generateSummarySection()
          ],
          footer: (currentPage, pageCount) => this.generateFooter(currentPage, pageCount),
          styles: this.getDocumentStyles(),
          pageMargins: [30, 40, 30, 40],
          pageSize: 'A4',
          pageOrientation: 'landscape'
        };

        pdfMake.createPdf(docDefinition).open();
        this.$toast?.success?.("PDF report generated successfully!");
      } catch (error) {
        console.error("Error generating PDF:", error);
        this.$toast?.error?.("Failed to generate PDF report");
      } finally {
        this.isExportingPDF = false;
      }
    },
    
    generateHeaderSection() {
      return {
        stack: [
          {
            image: this.base64log,
            width: 100,
            alignment: 'center',
            margin: [0, 0, 0, 8]
          },
          {
            text: this.reportTitle.toUpperCase(),
            style: 'header',
            alignment: 'center',
            margin: [0, 0, 0, 8]
          }
        ],
        alignment: 'center',
      };
    },
    
    generateReportInfoSection() {
      return {
        columns: [
          {
            width: '50%',
            stack: [
              { text: `Property: ${this.propertyName}`, style: 'propertyInfo' },
              { text: `Report: ${this.reportTitle}`, style: 'propertyInfo' }
            ]
          },
          {
            width: '50%',
            stack: [
              { text: `Period: ${this.formatDate(this.startDate)} to ${this.formatDate(this.endDate)}`, 
                style: 'propertyInfo', 
                alignment: 'right' },
              { text: `Generated: ${new Date().toLocaleDateString()}`, 
                style: 'propertyInfo', 
                alignment: 'right' }
            ]
          }
        ],
        margin: [0, 0, 0, 15]
      };
    },
    
    generateSummarySection() {
      const totalRental = this.formatCurrency(this.sumField('rentalAmount'));
      return {
        stack: [
          { text: 'Summary', style: 'sectionHeader' },
          { 
            columns: [
              { text: 'Total Rental:', style: 'summaryLabel', width: '80%' },
              { text: totalRental, style: 'summaryValue', width: '20%' }
            ],
            margin: [0, 0, 0, 3]
          },
          { 
            text: `Total Leases: ${this.reportData.length}`, 
            style: 'summaryLabel',
            margin: [0, 3, 0, 0]
          }
        ],
        margin: [0, 0, 0, 15]
      };
    },
    
    getTableLayout() {
      return {
        fillColor: function (rowIndex, node, columnIndex) {
          return (rowIndex === 0) ? '#F1C284' : null;
        },
        hLineColor: function (i, node) { return '#CCCCCC'; },
        vLineColor: function (i, node) { return '#CCCCCC'; }
      };
    },
    
    generateFooter(currentPage, pageCount) {
      return {
        stack: [
          {
            canvas: [
              {
                type: 'line',
                x1: 0,
                y1: 0,
                x2: 515,
                y2: 0,
                lineWidth: 0.5,
                lineColor: '#000000',
              },
            ],
            margin: [0, 0, 0, 3],
          },
          {
            text: 'GQ Property Group | 33 Donkin Street Central Port Elizabeth Eastern Cape\n0615175157 | rent@gqproperty.co.za',
            style: 'footer',
            alignment: 'center',
          },
          {
            text: `Page ${currentPage} of ${pageCount}`,
            fontSize: 7,
            alignment: 'right',
            margin: [0, 3, 10, 0],
          },
        ],
      };
    },
    
    getDocumentStyles() {
      return {
        header: { fontSize: 14, bold: true, color: '#000080', margin: [0, 0, 0, 8] },
        subheader: { fontSize: 12, bold: true, color: '#F1C284' },
        propertyInfo: { fontSize: 8, margin: [0, 0, 0, 3] },
        sectionHeader: { fontSize: 11, bold: true, color: '#000080', margin: [0, 0, 0, 8] },
        tableHeader: { bold: true, fontSize: 8, color: '#FFFFFF', alignment: 'center', fillColor: '#F1C284' },
        tableData: { fontSize: 7, alignment: 'center' },
        tableFooter: { fontSize: 7, bold: true, fillColor: '#E0E0E0', alignment: 'center' },
        summaryLabel: { fontSize: 8, bold: true },
        summaryValue: { fontSize: 8, bold: true, alignment: 'right' },
        footer: { fontSize: 8 }
      };
    }
  }
};
</script>

<style scoped>
/* Compact Card */
.compact-card {
  border-radius: 12px !important;
}

.compact-table-card {
  border-radius: 8px !important;
  border: 1px solid #e0e0e0 !important;
}

/* Compact Table */
.compact-table :deep(.v-data-table-header th) {
  font-size: 0.6rem !important;
  font-weight: 700 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.5px !important;
  padding: 6px 8px !important;
  background-color: #f0f0f0 !important;
  color: #000000 !important;
  border-bottom: 2px solid #ccc !important;
}

.compact-table :deep(.v-data-table-header th span) {
  color: #000000 !important;
}

.compact-table :deep(tbody td) {
  font-size: 0.7rem !important;
  padding: 4px 8px !important;
  color: #000000 !important;
}

.compact-table :deep(tbody td .v-chip) {
  font-size: 0.55rem !important;
  height: 20px !important;
}

/* Caption class */
.caption {
  font-size: 0.7rem !important;
}

/* Buttons */
.v-btn.caption {
  font-size: 0.65rem !important;
  letter-spacing: 0.3px;
  min-height: 30px !important;
  padding: 0 12px !important;
  text-transform: none !important;
}

.v-btn .v-icon {
  font-size: 16px !important;
}

/* Expired/Expiring styles */
.expired {
  background-color: #ffebee !important;
}
.expiring-soon {
  background-color: #fff8e1 !important;
}

/* Responsive */
@media (max-width: 768px) {
  .compact-table :deep(.v-data-table-header th) {
    font-size: 0.5rem !important;
    padding: 4px 4px !important;
  }
  
  .compact-table :deep(tbody td) {
    font-size: 0.6rem !important;
    padding: 3px 4px !important;
  }
}

@media print {
  .v-btn, .v-btn-toggle, .v-select, .v-expansion-panel {
    display: none !important;
  }
}
</style>